import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import CheckInFeedbackPanel from '../CheckInFeedbackPanel';
import CheckInTrendChart from './CheckInTrendChart';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import PhotoComparisonViewer from '../../coach/PhotoComparisonViewer';
import PoseSlotPicker from '../../comparison/PoseSlotPicker';
import { buildPair, defaultSecondPose } from '../../../lib/comparisonSlots';
import { measurementsApi } from '../../../api';
import { useAuth } from '../../../contexts/AuthContext';

const STANDARD_POSES = (t) => [
  { key: 'front', label: t('clientCheckIns.posesFrontRelaxed') },
  { key: 'side',  label: t('clientCheckIns.posesSideRelaxed') },
  { key: 'back',  label: t('clientCheckIns.posesBackRelaxed') },
];

const FLEX_POSES = (t) => [
  { key: 'front_flex', label: t('clientCheckIns.posesFrontFlex') },
  { key: 'side_flex',  label: t('clientCheckIns.posesSideFlex') },
  { key: 'back_flex',  label: t('clientCheckIns.posesBackFlex') },
  { key: 'relaxed',    label: t('clientCheckIns.posesFavorite') },
];

const MEASUREMENT_FIELDS = (t) => [
  { key: 'weight_kg',       label: t('clientCheckIns.statWeight'),         unit: 'kg' },
  { key: 'body_fat_percent',label: t('clientCheckIns.statBodyFat'),        unit: '%' },
  { key: 'chest_cm',        label: t('clientDetail.measurementChest'),     unit: 'cm' },
  { key: 'waist_cm',        label: t('clientDetail.measurementWaist'),     unit: 'cm' },
  { key: 'hips_cm',         label: t('clientDetail.measurementHips'),      unit: 'cm' },
  { key: 'shoulders_cm',    label: t('clientDetail.measurementShoulders'), unit: 'cm' },
  { key: 'left_arm_cm',     label: t('clientDetail.measurementLeftArm'),   unit: 'cm' },
  { key: 'right_arm_cm',    label: t('clientDetail.measurementRightArm'),  unit: 'cm' },
  { key: 'left_thigh_cm',   label: t('clientDetail.measurementLeftThigh'), unit: 'cm' },
  { key: 'right_thigh_cm',  label: t('clientDetail.measurementRightThigh'),unit: 'cm' },
];

// BEFORE/AFTER follow the DATES, not which control picked them.
//
// The Compare tab has one picker and it says "Compare against", not "Before",
// so the roles are implicit. Treating the picked check-in as `before` and the
// open one as `after` meant that opening an OLD check-in and comparing it with
// a NEWER one labelled the newer photo BEFORE and the older one AFTER — and the
// weight delta on the shared card came out with the wrong sign. Ordering by
// date makes the pair read correctly whichever one was opened first.
//
// MeasurementsPage deliberately does NOT do this: it has two pickers explicitly
// labelled Before and After, so there the user's choice is the instruction.
//
// Returns [before, after]. Exported for tests.
export function orderComparisonPair(picked, selected) {
  if (!picked || !selected) return [picked, selected];
  const pickedTime = new Date(picked.date).getTime();
  const selectedTime = new Date(selected.date).getTime();
  // An unparseable date falls back to the declared order rather than letting a
  // NaN comparison silently decide it.
  if (!Number.isFinite(pickedTime) || !Number.isFinite(selectedTime)) {
    return [picked, selected];
  }
  return pickedTime <= selectedTime ? [picked, selected] : [selected, picked];
}

export default function CheckInDetail({
  selectedCheckIn,
  allCheckIns,
  clientName,
  clientId,
  onSaveFeedback,
  onOpenLightbox,
  onSelect,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();

  // All hooks must run before any early return
  const [tab, setTab] = React.useState('detail');
  const [cmpPose, setCmpPose] = React.useState('front');
  const [cmpBeforeId, setCmpBeforeId] = React.useState('');
  const [cmpPairCount, setCmpPairCount] = React.useState(() => {
    try { return localStorage.getItem('trs_cmp_pairs') === '2' ? 2 : 1; } catch { return 1; }
  });
  const [cmpPoseAfter, setCmpPoseAfter] = React.useState('front');
  const [cmpSlot2, setCmpSlot2] = React.useState({ before: 'back', after: 'back' });
  const queryClient = useQueryClient();

  const checkInsWithPhotos = React.useMemo(
    () => (allCheckIns || []).filter(ci => ci.has_photos),
    [allCheckIns]
  );

  React.useEffect(() => {
    if (!selectedCheckIn) return;
    if (cmpBeforeId && allCheckIns?.some(c => c.id === cmpBeforeId && c.id !== selectedCheckIn.id)) return;
    const selIdx = checkInsWithPhotos.findIndex(c => c.id === selectedCheckIn.id);
    const candidate = checkInsWithPhotos[selIdx + 1] || checkInsWithPhotos.find(c => c.id !== selectedCheckIn.id);
    if (candidate) setCmpBeforeId(candidate.id);
  }, [selectedCheckIn, allCheckIns, checkInsWithPhotos, cmpBeforeId]);

  if (!selectedCheckIn) return null;
  if (!allCheckIns) return null;

  const ci = selectedCheckIn;
  const idx = allCheckIns.findIndex(c => c.id === ci.id);
  const prevCi = allCheckIns[idx + 1];

  const ALL_POSES = [
    { key: 'front',      label: t('clientCheckIns.posesFrontRelaxed') },
    { key: 'side',       label: t('clientCheckIns.posesSideRelaxed') },
    { key: 'back',       label: t('clientCheckIns.posesBackRelaxed') },
    { key: 'front_flex', label: t('clientCheckIns.posesFrontFlex') },
    { key: 'side_flex',  label: t('clientCheckIns.posesSideFlex') },
    { key: 'back_flex',  label: t('clientCheckIns.posesBackFlex') },
    { key: 'relaxed',    label: t('clientCheckIns.posesFavorite') },
  ];

  // Not memoised: this sits below the early returns above, so a hook here would
  // be a conditional hook (rules-of-hooks is an error in craco.config.js). It
  // is two date parses on every render, which is nothing.
  const cmpPicked = allCheckIns.find(c => c.id === cmpBeforeId);
  const [cmpBefore, cmpAfter] = orderComparisonPair(cmpPicked, selectedCheckIn);

  const buildLightboxPhotos = () => {
    const all = [...STANDARD_POSES(t), ...FLEX_POSES(t)];
    return all.filter(p => ci[`photo_${p.key}`]).map(p => ({
      url: ci[`photo_${p.key}`], label: p.label,
    }));
  };

  const hasStd = STANDARD_POSES(t).some(p => ci[`photo_${p.key}`]);
  const hasFlx = FLEX_POSES(t).some(p => ci[`photo_${p.key}`]);

  // Shared by both the 2-photo and 4-photo viewer branches below.
  const cachePatch = async (checkInId, poseKey, detection) => {
    try {
      await measurementsApi.patchKeypoints(checkInId, { [poseKey]: detection });
      if (clientId) queryClient.invalidateQueries({ queryKey: ['client-check-ins', clientId] });
    } catch (e) {
      console.warn('Failed to cache keypoints', e);
    }
  };

  return (
    <div className="space-y-4">
      <CheckInTrendChart
        checkIns={allCheckIns}
        selectedId={selectedCheckIn.id}
        onSelect={onSelect}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 w-full md:w-auto md:inline-grid">
          <TabsTrigger value="detail">{t('clientCheckIns.tabDetail', 'Detail')}</TabsTrigger>
          <TabsTrigger value="compare" disabled={checkInsWithPhotos.length < 2}>
            {t('clientCheckIns.tabCompareVs', 'Compare vs…')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="detail" className="mt-4 space-y-4">
          {/* Photos */}
          {(hasStd || hasFlx) && (
            <div className="space-y-2">
              {hasStd && (
                <div className="grid grid-cols-3 gap-2">
                  {STANDARD_POSES(t).map(({ key, label }) => (
                    <div key={key} className="aspect-[9/16] rounded-lg overflow-hidden bg-muted/40 relative md:min-h-[340px]">
                      {ci[`photo_${key}`]
                        ? <>
                            <img
                              src={ci[`photo_${key}`]}
                              alt={label}
                              className="w-full h-full object-cover cursor-pointer"
                              onClick={() => { const photos = buildLightboxPhotos(); const index = photos.findIndex(p => p.url === ci[`photo_${key}`]); onOpenLightbox(photos, index); }}
                            />
                            <div className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-black/60 text-white text-[8px] px-1.5 rounded uppercase">{label}</div>
                          </>
                        : <div className="w-full h-full flex items-center justify-center text-[9px] text-muted-foreground/50 uppercase">{label}</div>}
                    </div>
                  ))}
                </div>
              )}
              {hasFlx && (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {FLEX_POSES(t).map(({ key, label }) => (
                    <div key={key} className="aspect-[9/16] rounded-lg overflow-hidden bg-muted/40 relative md:min-h-[340px]">
                      {ci[`photo_${key}`]
                        ? <>
                            <img
                              src={ci[`photo_${key}`]}
                              alt={label}
                              className="w-full h-full object-cover cursor-pointer"
                              onClick={() => { const photos = buildLightboxPhotos(); const index = photos.findIndex(p => p.url === ci[`photo_${key}`]); onOpenLightbox(photos, index); }}
                            />
                            <div className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-black/60 text-white text-[8px] px-1.5 rounded uppercase whitespace-nowrap">{label}</div>
                          </>
                        : <div className="w-full h-full flex items-center justify-center text-[9px] text-muted-foreground/50 uppercase">{label}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Measurements */}
          {MEASUREMENT_FIELDS(t).some(f => ci[f.key] != null) && (
            <div className="grid grid-cols-3 md:grid-cols-5 gap-1.5">
              {MEASUREMENT_FIELDS(t).filter(f => ci[f.key] != null).map(f => {
                const prevVal = prevCi?.[f.key];
                const diff = prevVal != null ? ci[f.key] - prevVal : null;
                return (
                  <div key={f.key} className="rounded-lg bg-muted/40 px-2 py-1.5 text-center">
                    <p className="text-xs font-mono font-bold">{ci[f.key]}<span className="text-[9px] text-muted-foreground ml-0.5">{f.unit}</span></p>
                    {diff !== null && Math.abs(diff) > 0.05 && (
                      <p className={`text-[9px] font-semibold ${f.key === 'weight_kg' || f.key === 'body_fat_percent' ? (diff < 0 ? 'text-green-500' : 'text-red-400') : (diff > 0 ? 'text-green-500' : 'text-red-400')}`}>
                        {diff > 0 ? '+' : ''}{diff.toFixed(1)}
                      </p>
                    )}
                    <p className="text-[9px] text-muted-foreground leading-none mt-0.5">{f.label}</p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Client note */}
          {ci.notes && (
            <div className="rounded-xl bg-muted/40 px-3 py-2.5">
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">{t('clientCheckIns.clientNotes')}</p>
              <p className="text-xs leading-relaxed">{ci.notes}</p>
            </div>
          )}

          {/* Coach feedback */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-primary uppercase">{t('clientCheckIns.coachFeedback')}</p>
            {ci.coach_feedback ? (
              <div className="rounded-xl bg-muted/40 px-3 py-2.5 space-y-2">
                {[
                  { key: 'overall',              label: t('clientCheckIns.feedbackOverall') },
                  { key: 'weight_comment',       label: t('clientCheckIns.feedbackWeight') },
                  { key: 'measurements_comment', label: t('clientCheckIns.feedbackMeasurements') },
                  { key: 'photos_comment',       label: t('clientCheckIns.feedbackPhotos') },
                  { key: 'adherence_comment',    label: t('clientCheckIns.feedbackAdherence') },
                  { key: 'sleep_comment',        label: t('clientCheckIns.feedbackSleep') },
                  { key: 'mood_comment',         label: t('clientCheckIns.feedbackMood') },
                ].filter(f => ci.coach_feedback[f.key]).map(f => (
                  <div key={f.key}>
                    <p className="text-[9px] text-muted-foreground uppercase font-semibold">{f.label}</p>
                    <p className="text-xs leading-relaxed">{ci.coach_feedback[f.key]}</p>
                  </div>
                ))}
              </div>
            ) : ci.coach_notes ? (
              <div className="rounded-xl bg-muted/40 px-3 py-2.5">
                <p className="text-[9px] text-muted-foreground uppercase font-semibold mb-1">{t('clientCheckIns.feedbackOverall')}</p>
                <p className="text-xs leading-relaxed">{ci.coach_notes}</p>
              </div>
            ) : (
              <CheckInFeedbackPanel
                checkIn={ci}
                clientName={clientName || 'client'}
                onSave={(feedbackData) => onSaveFeedback(ci.id, feedbackData)}
                onMarkReviewed={() => onSaveFeedback(ci.id, { coach_notes: '' })}
              />
            )}
          </div>
        </TabsContent>

        <TabsContent value="compare" className="mt-4">
          {checkInsWithPhotos.length < 2 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-sm text-muted-foreground">{t('clientCheckIns.noPhotosComparison')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground">
                  {t('clientCheckIns.compareAgainst', 'Compare against')}
                </Label>
                <Select value={cmpBeforeId} onValueChange={setCmpBeforeId}>
                  <SelectTrigger className="h-11 md:h-8 text-xs mt-0.5 w-full md:w-64">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {checkInsWithPhotos
                      .filter(ciW => ciW.id !== selectedCheckIn.id)
                      .map(ciW => (
                        <SelectItem key={ciW.id} value={ciW.id}>{ciW.date?.slice(0, 10)}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <PoseSlotPicker
                pairCount={cmpPairCount}
                onPairCount={(n) => {
                  setCmpPairCount(n);
                  try { localStorage.setItem('trs_cmp_pairs', String(n)); } catch {}
                  if (n === 2) {
                    setCmpPoseAfter(cmpPose);
                    const second = defaultSecondPose(cmpBefore, cmpAfter, cmpPose);
                    setCmpSlot2({ before: second, after: second });
                  }
                }}
                slots={{ r1b: cmpPose, r1a: cmpPoseAfter, r2b: cmpSlot2.before, r2a: cmpSlot2.after }}
                onSlots={(s) => {
                  setCmpPose(s.r1b);
                  setCmpPoseAfter(s.r1a);
                  setCmpSlot2({ before: s.r2b, after: s.r2a });
                }}
                beforeCheckIn={cmpBefore}
                afterCheckIn={cmpAfter}
              />

              {cmpPairCount === 2 ? (
                <div className="mt-3">
                  <PhotoComparisonViewer
                    pairs={[
                      buildPair('r1', cmpPose, cmpPoseAfter, cmpBefore, cmpAfter),
                      buildPair('r2', cmpSlot2.before, cmpSlot2.after, cmpBefore, cmpAfter),
                    ]}
                    handle={user?.username || ''}
                    coachMode
                    onKeypointsComputed={cachePatch}
                  />
                </div>
              ) : (
                <Tabs value={cmpPose} onValueChange={setCmpPose} className="mt-3">
                  <div className="overflow-x-auto">
                    <TabsList className="flex w-max gap-0 h-8">
                      {ALL_POSES.map(p => (
                        <TabsTrigger key={p.key} value={p.key} className="text-xs px-2 h-7 whitespace-nowrap">{p.label}</TabsTrigger>
                      ))}
                    </TabsList>
                  </div>
                  {ALL_POSES.map(({ key: pose }) => (
                    <TabsContent key={pose} value={pose} className="mt-3">
                      {cmpBefore && cmpBefore[`photo_${pose}`] && cmpAfter[`photo_${pose}`] ? (
                        <PhotoComparisonViewer
                          pairs={[{
                            id: `${pose}-1`,
                            poseType: { before: pose, after: pose },
                            before: {
                              photoUrl: cmpBefore[`photo_${pose}`],
                              keypoints: cmpBefore.photo_keypoints?.[pose] || null,
                              checkInId: cmpBefore.id,
                              weightKg: cmpBefore.weight_kg,
                              bodyFat: cmpBefore.body_fat_percent,
                              date: cmpBefore.date,
                            },
                            after: {
                              photoUrl: cmpAfter[`photo_${pose}`],
                              keypoints: cmpAfter.photo_keypoints?.[pose] || null,
                              checkInId: cmpAfter.id,
                              weightKg: cmpAfter.weight_kg,
                              bodyFat: cmpAfter.body_fat_percent,
                              date: cmpAfter.date,
                            },
                          }]}
                          handle={user?.username || ''}
                          coachMode
                          onKeypointsComputed={cachePatch}
                        />
                      ) : (
                        <p className="text-center text-xs text-muted-foreground py-4">{t('clientCheckIns.noPosePictures')}</p>
                      )}
                    </TabsContent>
                  ))}
                </Tabs>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
